"use client";

import React from "react";
import { useAppData } from "@/store/appStore";
import { useConfig } from "@/hooks/useConfig";
import { Logo } from "@/design-elements/logo";
import { trackEvent, GTMEvent } from "@/lib/utils/gtm-events";

interface StepDeviceSelectionProps {
	onComplete: (selection: "cars" | "devices") => void;
}

interface DeviceOption {
	id: "cars" | "devices";
	title: string;
	description: string;
	imageSrc: string;
	imageSrcMobile: string;
	imageAlt: string;
}

const DEVICE_SELECTION_COPY = {
	heroTitle: "Take your pick",
};

const DEVICE_OPTIONS: DeviceOption[] = [
	{
		id: "cars",
		title: "Cars",
		description:
			"Save upto 30% compared to car loan.\nCar is registered in your name.",
		imageSrc: "/onboarding/car.png",
		imageSrcMobile: "/onboarding/car.svg",
		imageAlt: "Cars",
	},
	{
		id: "devices",
		title: "Devices",
		description:
			"Save up to 40% compared to retail prices. Damage and theft protection included.",
		imageSrc: "/onboarding/device.png",
		imageSrcMobile: "/onboarding/devices.svg",
		imageAlt: "Devices",
	},
];

const DeviceCard: React.FC<{
	option: DeviceOption;
	onSelect: (selection: "cars" | "devices") => void;
}> = ({ option, onSelect }) => {
	const lines = option.description.split("\n");
	return (
		<button
			id={`onboarding_choose_${option.id}`}
			type="button"
			onClick={() => {
				trackEvent(GTMEvent.DEVICE_SELECTION, { selection: option.id });
				onSelect(option.id);
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") onSelect(option.id);
			}}
			className="relative flex w-full flex-col overflow-hidden rounded-[12px] bg-white border border-[var(--colors-automint-neutral-300)] md:bg-[var(--automint-black)] md:border-[var(--colors-automint-neutral-800)] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color-mix(in_srgb,var(--colors-automint-neon-500)_40%,transparent)] hover:border-[var(--colors-automint-neutral-500)] md:hover:border-[var(--automint-onboarding-disabled-bg)] transition-colors"
		>
			{/* Text on top */}
			<div className="flex flex-col gap-1 px-5 md:px-5 pt-4 md:pt-4 pb-2 md:pb-1">
				<h3 className="text-[18px] md:text-[18px]  text-[var(--automint-black)] md:text-white leading-tight">
					{option.title}
				</h3>
				<div className="text-[13px] md:text-[12px] leading-snug text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]">
					{lines.map((line, idx) => (
						<p key={`${option.id}-${idx}`} className="m-0">
							{line}
						</p>
					))}
				</div>
			</div>
			{/* Image below text — SVG for mobile (white theme), PNG for desktop (dark theme) */}
			<div className="relative w-full h-[110px] sm:h-[170px] md:h-[140px] 2xl:h-[180px] overflow-hidden">
				<img
					src={option.imageSrcMobile}
					alt={option.imageAlt}
					className="md:hidden absolute inset-x-0 bottom-0 w-full h-full object-contain object-bottom"
				/>
				<img
					src={option.imageSrc}
					alt={option.imageAlt}
					className="hidden md:block absolute inset-x-0 bottom-0 w-full h-full object-contain object-bottom"
				/>
			</div>
		</button>
	);
};

const StepDeviceSelection: React.FC<StepDeviceSelectionProps> = ({
	onComplete,
}) => {
	const [appData] = useAppData();
	const { isConfigReady } = useConfig();

	const showCar = appData?.showCar;
	const showDevice = appData?.showDevice;

	if (isConfigReady) {
		if (showCar && !showDevice) {
			onComplete("cars");
			return null;
		}
		if (!showCar && showDevice) {
			onComplete("devices");
			return null;
		}
	}

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-y-auto md:overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px] leading-tight">
							{DEVICE_SELECTION_COPY.heroTitle}
						</h1>
					</div>

					<div className="mt-6 flex flex-col gap-3 md:gap-4">
						{DEVICE_OPTIONS.map((option) => (
							<DeviceCard
								key={option.id}
								option={option}
								onSelect={onComplete}
							/>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default StepDeviceSelection;
